import type { ContentBlock } from './api';

const MAX_TEXT_LENGTH = 2000;
export const CONTENT_BLOCK_MEDIA_TITLE_PREFIX = '__content_block__/';

const blockTextKeys = ['text', 'title', 'caption', 'content'] as const;

const trimText = (value: string, limit = MAX_TEXT_LENGTH): string => {
  if (value.length <= limit) return value;
  return `${value.slice(0, limit)}...`;
};

export const parseContentBlocksInput = (raw: string): ContentBlock[] | null => {
  const value = raw.trim();
  if (!value) return null;

  const parsed: unknown = JSON.parse(value);
  if (!Array.isArray(parsed)) {
    throw new Error('Content blocks must be a JSON array.');
  }

  const blocks = parsed as ContentBlock[];
  for (const block of blocks) {
    if (!block || typeof block !== 'object') {
      throw new Error('Each content block must be an object.');
    }
    if (typeof block.type !== 'string' || !block.type.trim()) {
      throw new Error('Each content block must contain non-empty "type".');
    }
    if (typeof block.id !== 'string' || !block.id.trim()) {
      throw new Error('Each content block must contain non-empty "id".');
    }
  }

  return blocks;
};

export const stringifyContentBlocks = (blocks?: ContentBlock[] | null): string => {
  if (!blocks || !blocks.length) return '';
  return JSON.stringify(blocks, null, 2);
};

export const extractTextFromContentBlocks = (blocks?: ContentBlock[] | null): string => {
  if (!blocks || !blocks.length) return '';

  const parts: string[] = [];
  for (const block of blocks) {
    for (const key of blockTextKeys) {
      const value = block[key];
      if (typeof value === 'string' && value.trim()) {
        parts.push(value.trim());
      }
    }

    if (Array.isArray(block.items)) {
      for (const item of block.items) {
        if (typeof item === 'string' && item.trim()) {
          parts.push(item.trim());
          continue;
        }
        if (item && typeof item === 'object') {
          const title = (item as Record<string, unknown>).title;
          if (typeof title === 'string' && title.trim()) {
            parts.push(title.trim());
          }
        }
      }
    }
  }

  return trimText(parts.join('\n\n').trim());
};

export const isContentBlockMediaTitle = (title?: string | null): boolean =>
  typeof title === 'string' && title.startsWith(CONTENT_BLOCK_MEDIA_TITLE_PREFIX);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const toText = (value: unknown): string => (typeof value === 'string' ? value : '');

export const renderContentBlocksHtml = (blocks?: ContentBlock[] | null): string => {
  if (!blocks?.length) return '';

  return blocks
    .map((block) => {
      if (block.type === 'paragraph') {
        const text = toText(block.text).trim();
        return text ? `<p>${escapeHtml(text)}</p>` : '';
      }
      if (block.type === 'heading') {
        const levelRaw = Number(block.level) || 2;
        const level = Math.max(1, Math.min(4, levelRaw));
        const text = toText(block.text).trim();
        return text ? `<h${level}>${escapeHtml(text)}</h${level}>` : '';
      }
      if (block.type === 'list') {
        const style = toText(block.style);
        const items = Array.isArray(block.items) ? block.items : [];
        const rows = items.map((item) => `<li>${escapeHtml(String(item))}</li>`).join('');
        if (!rows) return '';
        if (style === 'ordered') return `<ol>${rows}</ol>`;
        return `<ul>${rows}</ul>`;
      }
      if (block.type === 'quote') {
        const text = toText(block.text).trim();
        if (!text) return '';
        return `<blockquote><p>${escapeHtml(text)}</p></blockquote>`;
      }
      if (block.type === 'callout') {
        const text = toText(block.text).trim();
        const title = toText(block.title).trim();
        if (!text && !title) return '';
        return `<div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">${title ? `<strong>${escapeHtml(title)}</strong><br/>` : ''}${escapeHtml(text)}</div>`;
      }
      if (block.type === 'image') {
        const url = toText(block.url).trim();
        if (!url) return '';
        const alt = toText(block.alt);
        const caption = toText(block.caption).trim();
        return `<figure><img class="w-full rounded-lg border border-slate-200" src="${escapeHtml(url)}" alt="${escapeHtml(alt)}"/>${caption ? `<figcaption class="text-xs text-slate-500 mt-1">${escapeHtml(caption)}</figcaption>` : ''}</figure>`;
      }
      if (block.type === 'gallery') {
        const items = Array.isArray(block.items) ? block.items : [];
        const html = items
          .map((itemValue) => {
            const item = itemValue as Record<string, unknown>;
            const url = toText(item.url).trim();
            if (!url) return '';
            const alt = toText(item.alt);
            const caption = toText(item.caption).trim();
            return `<figure class="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden"><img class="w-full h-44 object-cover" src="${escapeHtml(url)}" alt="${escapeHtml(alt)}"/>${caption ? `<figcaption class="px-2 py-1 text-xs text-slate-500">${escapeHtml(caption)}</figcaption>` : ''}</figure>`;
          })
          .join('');
        return html ? `<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">${html}</div>` : '';
      }
      if (block.type === 'embed') {
        const url = toText(block.url).trim();
        if (!url) return '';
        return `<p><a class="text-primary underline" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(toText(block.title) || url)}</a></p>`;
      }
      if (block.type === 'table') {
        const columns = Array.isArray(block.columns) ? block.columns : [];
        const rows = Array.isArray(block.rows) ? block.rows : [];
        if (!columns.length) return '';
        const head = columns.map((column) => `<th class="border border-slate-200 px-2 py-1 text-left">${escapeHtml(String(column))}</th>`).join('');
        const body = rows
          .map((rowValue) => {
            const row = Array.isArray(rowValue) ? rowValue : [];
            return `<tr>${row.map((cell) => `<td class="border border-slate-200 px-2 py-1">${escapeHtml(String(cell))}</td>`).join('')}</tr>`;
          })
          .join('');
        return `<div class="overflow-auto"><table class="min-w-full text-sm"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
      }
      if (block.type === 'related_links') {
        const items = Array.isArray(block.items) ? block.items : [];
        const links = items
          .map((itemValue) => {
            const item = itemValue as Record<string, unknown>;
            const title = toText(item.title).trim();
            const url = toText(item.url).trim();
            if (!title || !url) return '';
            return `<li><a class="text-primary underline" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(title)}</a></li>`;
          })
          .join('');
        return links ? `<ul>${links}</ul>` : '';
      }
      if (block.type === 'cta') {
        const title = toText(block.title).trim();
        const text = toText(block.text).trim();
        const buttonText = toText(block.button_text).trim();
        const buttonUrl = toText(block.button_url).trim();
        if (!title && !text) return '';
        return `<div class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"><div class="font-semibold">${escapeHtml(title)}</div><p>${escapeHtml(text)}</p>${buttonText && buttonUrl ? `<a class="inline-flex mt-2 rounded bg-primary text-white px-3 py-1 text-xs" href="${escapeHtml(buttonUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(buttonText)}</a>` : ''}</div>`;
      }
      if (block.type === 'code') {
        const content = toText(block.content).trim();
        return content ? `<pre class="rounded-lg bg-slate-900 text-slate-100 p-3 overflow-auto">${escapeHtml(content)}</pre>` : '';
      }
      if (block.type === 'divider') {
        return '<hr class="my-4 border-slate-200"/>';
      }
      if (block.type === 'tags') {
        const items = Array.isArray(block.items) ? block.items : [];
        const tags = items
          .map((itemValue) => {
            const item = itemValue as Record<string, unknown>;
            const label = toText(item.slug) || toText(item.name);
            return label ? `<span class="text-xs bg-primary/10 text-primary px-2 py-1 rounded">#${escapeHtml(label)}</span>` : '';
          })
          .join('');
        return tags ? `<div class="flex flex-wrap gap-2">${tags}</div>` : '';
      }
      return '';
    })
    .filter(Boolean)
    .join('');
};
